// src/context/LocationContext.js
import React, { createContext, useState, useEffect } from "react";

export const LocationContext = createContext();

export const LocationProvider = ({ children }) => {
    const [selectedAddress, setSelectedAddress] = useState(
        localStorage.getItem("selectedAddress") || ""
    );
    const [cityName, setCityName] = useState(
        localStorage.getItem("cityName") || ""
    );
    const [latitude, setLatitude] = useState(
        parseFloat(localStorage.getItem("latitude")) || null
    );
    const [longitude, setLongitude] = useState(
        parseFloat(localStorage.getItem("longitude")) || null
    );
    const [radius, setRadius] = useState(
        parseInt(localStorage.getItem("radius")) || 7 // Default 7km
    );
    const [isManualLocation, setIsManualLocation] = useState(
        sessionStorage.getItem("isManualLocation") === "true"
    );

    // Sync with localStorage
    useEffect(() => {
        if (selectedAddress) localStorage.setItem("selectedAddress", selectedAddress);
        else localStorage.removeItem("selectedAddress");

        if (cityName) localStorage.setItem("cityName", cityName);
        else localStorage.removeItem("cityName");

        if (latitude) localStorage.setItem("latitude", latitude);
        else localStorage.removeItem("latitude");

        if (longitude) localStorage.setItem("longitude", longitude);
        else localStorage.removeItem("longitude");

        localStorage.setItem("radius", radius);
        sessionStorage.setItem("isManualLocation", isManualLocation);
    }, [selectedAddress, cityName, latitude, longitude, radius, isManualLocation]);

    // ✅ Only auto-detect location if not already stored
    useEffect(() => {
        const hasSavedPos = localStorage.getItem("latitude") && localStorage.getItem("longitude");
        if (!hasSavedPos) {
            handleAutoDetect();
        }
    }, []);

    const reverseGeocodeLatLng = async (lat, lng) => {
        try {
            const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyCOBxdTr-iPKepu-nUpPRHL9jTte7UTcNQ";
            const response = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_KEY}`
            );
            const data = await response.json();

            if (data.status === "OK" && data.results[0]) {
                const components = data.results[0].address_components;
                const getComp = (type) => components.find((c) => c.types.includes(type))?.long_name || "";

                const city = getComp("locality") ||
                    getComp("sublocality") ||
                    getComp("sublocality_level_1") ||
                    getComp("administrative_area_level_3") ||
                    getComp("administrative_area_level_2") ||
                    "Detected Location";

                setCityName(city);
                setSelectedAddress(data.results[0].formatted_address);
            }
        } catch (err) {
            console.error("Geocoding error:", err);
        }
    };

    const handleAutoDetect = () => {
        if (!navigator.geolocation) return;

        const performDetection = (highAccuracy = true) => {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const lat = position.coords.latitude;
                    const lng = position.coords.longitude;

                    setLatitude(lat);
                    setLongitude(lng);

                    await reverseGeocodeLatLng(lat, lng);
                },
                (error) => {
                    if (highAccuracy && (error.code === error.TIMEOUT || error.code === error.POSITION_UNAVAILABLE)) {
                        console.warn("⚠️ High accuracy auto-location failed, retrying with normal accuracy...");
                        performDetection(false);
                        return;
                    }
                    console.warn("Auto-location failed or denied:", error.message);

                    // 🌍 Laptop Fallback: Use IP-based location if GPS/Wi-Fi positioning fails entirely
                    if (error.code !== error.PERMISSION_DENIED) {
                        console.log("Falling back to IP-based location detection...");
                        fetch("https://ipapi.co/json/")
                            .then(res => res.json())
                            .then(async (data) => {
                                if (data.latitude && data.longitude && !data.error) {
                                    setLatitude(data.latitude);
                                    setLongitude(data.longitude);
                                    await reverseGeocodeLatLng(data.latitude, data.longitude);
                                }
                            })
                            .catch(err => console.error("IP Location fallback failed:", err));
                    }
                },
                { enableHighAccuracy: highAccuracy, timeout: highAccuracy ? 8000 : 15000, maximumAge: 0 }
            );
        };

        performDetection(true);
    };

    const value = React.useMemo(() => ({
        selectedAddress, setSelectedAddress,
        cityName, setCityName,
        latitude, setLatitude,
        longitude, setLongitude,
        radius, setRadius,
        isManualLocation, setIsManualLocation
    }), [
        selectedAddress,
        cityName,
        latitude,
        longitude,
        radius,
        isManualLocation
    ]);

    return (
        <LocationContext.Provider value={value}>
            {children}
        </LocationContext.Provider>
    );
};
